import * as React from 'react';
import { cn } from '../../lib/cn';

interface TabItem {
  value: string;
  label: React.ReactNode;
  count?: number;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  items: TabItem[];
  className?: string;
  variant?: 'default' | 'pill';
}

export function Tabs({ value, onChange, items, className, variant = 'default' }: TabsProps) {
  if (variant === 'pill') {
    return (
      <div className={cn('inline-flex rounded-md bg-muted p-1 text-sm', className)} role="tablist">
        {items.map((item) => {
          const isActive = item.value === value;
          return (
            <button
              key={item.value}
              role="tab"
              aria-selected={isActive}
              disabled={item.disabled}
              onClick={() => onChange(item.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-sm px-3 py-1 font-medium transition-colors',
                isActive
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
                item.disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {item.icon}
              {item.label}
              {typeof item.count === 'number' && (
                <span className={cn('ml-1 rounded-full px-1.5 py-0.5 text-[10px]', isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <div className={cn('border-b border-border', className)} role="tablist">
      <div className="flex flex-wrap gap-4 overflow-x-auto">
        {items.map((item) => {
          const isActive = item.value === value;
          return (
            <button
              key={item.value}
              role="tab"
              aria-selected={isActive}
              disabled={item.disabled}
              onClick={() => onChange(item.value)}
              className={cn(
                'relative inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 -mb-px px-1 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                item.disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {item.icon}
              {item.label}
              {typeof item.count === 'number' && (
                <span className={cn('ml-1 rounded-full px-1.5 py-0.5 text-[10px]', isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
